/* eslint-disable no-new-func */
/* eslint-disable quotes */
import { store } from '..'
import { IPcr } from '../features/pcr/store/pcr.types'
import { runRule } from '../features/rules/store/rules.actions'
import { RulesErrors, Rules, IRuleError, GroupedRuleErrors, RulesIndex, IValidationOutputObject, IValidationInputObject, ValidationErrorMap } from '../features/rules/store/rules.types'
import { getCurrentDate, isValidNemsisDate, isValidNemsisDOB, isValidZollNowDate, isValidZollTime } from './dateTime.util'
import Logger from '../services/logger.service'
import { cloneDeep } from 'lodash'
import { getRulesUtilsModuleSelector } from '../features/options/selectors'
import { ISpecialReport } from '../features/specialReports/store/specialReports.types'

export const patientSearchPaths = ['ePatient', 'patientDemographics', 'patientMedications', 'patientMedicalHistory', 'patientImmunizations', 'insuranceInformation', 'relationshipsAndGuarantors', 'pcsInformation', 'authorizationInformation', 'cmsInformation', 'paymentInformation', 'employer']

export const clearPatientSearchErrors = (errors: ValidationErrorMap) => {
  return clearValidationErrorsByPathList(errors, patientSearchPaths)
}

export const mapRuleErrors = (errorArray: RulesErrors): ValidationErrorMap => {
  const errMap: ValidationErrorMap = {}

  if (errorArray) {
    for (const error of errorArray) {
      if (!errMap[error.sourceType]) {
        errMap[error.sourceType] = []
      }
      errMap[error.sourceType].push(error)
    }
  }

  return errMap
}

export const clearValidationErrorsByCardinalityKey = (errors: ValidationErrorMap, key: string): ValidationErrorMap => {
  const flatErrArr = Object.values(errors).flat()

  flatErrArr.filter((err) => { return err.cardinalityKey != key})
  return mapRuleErrors(flatErrArr)
}

export const clearValidationErrorsByPathList = (errors: ValidationErrorMap, pathList: string[]): ValidationErrorMap => {
  const filteredObjects: RulesErrors = []

  for (const errorKey of Object.keys(errors)) {
    if (errors[errorKey]) {
      for (const err of errors[errorKey]) {
        let match = false
        pathList.forEach((path) => {
          if (err.id.includes(path)) {
            match = true
          }
        })
        if (!match) {
          filteredObjects.push(err)
        }
      }
    }
  }

  return mapRuleErrors(filteredObjects)
}

export const getErrorCount = (errors: ValidationErrorMap, action: string) => {
  let count = 0

  const errArrFlat = Object.values(errors).flat()
  errArrFlat.forEach((ruleErr: IRuleError) => {
    for (const dp of ruleErr.dataPoints) {
      if (dp.reduxAction === action) {
        count++
      }
    }
  })

  return count
}

export const getErrorMessage = (errors: ValidationErrorMap, reduxAction: string, cardinalityKey: string | undefined, showReduxStateErrorsOnlyFlag: boolean | undefined) => {
  const flatErrArr = Object.values(errors).flat()


  const errorList = cardinalityKey
    ? [...flatErrArr.filter((error: IRuleError) => error.dataPointCardinalityKey ? error.dataPointCardinalityKey === cardinalityKey : error.cardinalityKey === cardinalityKey)]
    : [...flatErrArr.filter((error: IRuleError) => error.cardinalityKey === '')]



  for (const error of errorList) {
    for (const dataPoint of error.dataPoints) {
      if (dataPoint.reduxAction === reduxAction
        && ((showReduxStateErrorsOnlyFlag && error.reduxStateOnlyFlag)
          || (!showReduxStateErrorsOnlyFlag && !error.reduxStateOnlyFlag))) {
        return error.errorMessage
      }
    }
  }
}

export const removeValidationError = (errors: ValidationErrorMap, reduxAction: string, cardinalityKey: string | undefined) => {
  const keys = Object.keys(errors)

  for (const errorType of keys) {
    for (const err of errors[errorType]) {
      if (err.id === reduxAction && err.cardinalityKey === cardinalityKey) {
        errors[errorType].slice(errors[errorType].indexOf(err), 1)
      }
    }
  }
}

export const groupedByParentLabel = (errors: RulesErrors) => {
  const errorsCopy = [...errors]

  for (let i = errorsCopy.length - 1; i >= 0; i--) {
    if (errorsCopy[i].reduxStateOnlyFlag || errorsCopy[i].hideOnUploadPageFlag) {
      errorsCopy.splice(i, 1)
    }
  }
  return errorsCopy.reduce((results: GroupedRuleErrors, error) => {
    if (error.priority === 0) {
      if (!results['Required']) {
        results = { Required: [], ...results }
      }
      results['Required'].push(error)
    } else {
      const sourceType = error.sourceType.toUpperCase()
      const parentLabel = (sourceType === 'STANDARD' || sourceType === 'XSD' ? error.dataPoints[0]?.parentLabel 
        : sourceType === 'SCHEMATRON' ? 'National Rules' 
          : sourceType === 'EMSCHARTS' ? 'Service Rules' 
            : `${sourceType} State Rules`)

      if (!results[parentLabel || ""]) {
        results[parentLabel || ""] = []
      }
      results[parentLabel || ""].push(error)
    }
    return results
  }, {})
}

export const createIndexedRules = (rules: Rules) => {
  const indexedRules: RulesIndex = {}

  rules.forEach((rule) => {
    if (rule.dataPoints.length === 0) {
      if (!indexedRules.other) {
        indexedRules.other = []
      }

      indexedRules.other.push(rule)
    }

    rule.dataPoints?.forEach((dataPoint) => {
      if (!indexedRules[dataPoint.reduxAction || ""]) {
        indexedRules[dataPoint.reduxAction || ""] = []
      }
      indexedRules[dataPoint.reduxAction || ""].push(rule)
    })
  })

  return indexedRules
}

export const startRulesValidation = () => {
  if (store.getState().rules.dispatchedActions.length > 0) {
    store.dispatch(runRule())
  }
}

export const runRulesValidation = (
  rules: Rules,
  pcrData: IPcr,
  dispatchedActions?: string[]
): { rulesErrors: RulesErrors; idsOfRulesExecuted: string[] } => {
  const RulesUtils = getRulesUtilsModuleSelector(store.getState())
  let data: IPcr = cloneDeep(pcrData)
  const rulesErrors: RulesErrors = []
  const idsOfRulesExecuted: string[] = []
  let rulesToCheck: Rules = []

  if (dispatchedActions) {
    for (const rule of rules) {
      for (const dataPoint of rule.dataPoints) {
        if (dispatchedActions.find((a) => (a === dataPoint.reduxAction))) {
          rulesToCheck.push(rule)
          idsOfRulesExecuted.push(rule.id)
        }
      }
    }
  } else {
    rulesToCheck = rules
  }

  const executeScript = (
    validationInputObject: IValidationInputObject
  ): [IValidationOutputObject] => {
    try {
      const fn = new Function(
        'RulesUtils',
        'isValidZollNowDate',
        'isValidZollTime',
        'isValidNemsisDate',
        'isValidNemsisDOB',
        'store',
        'getCurrentDate',
        'validationInputObject',
        validationInputObject.ruleScript
      )
      const result: [IValidationOutputObject] = fn(RulesUtils, isValidZollNowDate, isValidZollTime, isValidNemsisDate, isValidNemsisDOB, store, getCurrentDate)(validationInputObject)
      return result
    } catch (error) {
      const result: [IValidationOutputObject] = [{
        successful: false,
        cardinalityPathIndexMap: new Map<string, number>(),
        // TODO: Without the individual error object error message, how do we indicate an exception thrown while executing? Or do we care?
      }]
      Logger.errorObj(`Error executing rules script for rule: ${validationInputObject.ruleId}`, error)
      return result
    }
  }

  for (const rule of rulesToCheck) {

    if (rule.id === 'ResetPCRData') {
      data = cloneDeep(pcrData)
    }

    const validationInputObject: IValidationInputObject = {
      ruleId: rule.id,
      data: data,
      ruleScript: rule.script,
      validationFieldLabelMap: new Map<string, string>(),
      cardinalityIndexMap: new Map<string, number>()
    }

    const specialReport = store.getState().specialReports.specialReportsList?.some((r: ISpecialReport) => r.ruleId === rule.id && r.completed)

    if (rule.sourceType === 'emsCharts' && specialReport) {
      continue
    }

    const results = executeScript(validationInputObject)

    for (const result of results) {
      if (!result.successful) {
        let dateTimeError
        if(rule.errorMessage?.includes('format-dateTime')){
          dateTimeError = RulesUtils.formatDateTimeErrorMessage(rule.errorMessage)  
        }
        const error: IRuleError = {
          id: rule.id,
          priority: rule.priority,
          errorMessage: dateTimeError || result.errorMessage || rule.errorMessage || '',
          dataPoints: rule.dataPoints,
          sourceType: rule.sourceType,
          errorType: rule.errorType,
          cardinalityKey: result.cardinalityKey ? result.cardinalityKey : '',
          reduxStateOnlyFlag: !!rule.triggerOnStateChangeFlag,
          specialReportCategoryId: rule.specialReportCategoryId,
        }
        if (result.dataPointCardinalityKey) {
          error.dataPointCardinalityKey = result.dataPointCardinalityKey
        }
        if (rule.hideOnUploadPageFlag) {
          error.hideOnUploadPageFlag = rule.hideOnUploadPageFlag
        }

        rulesErrors.push(error)
      }
    }
  }

  return { rulesErrors, idsOfRulesExecuted }
}

